import supabase from "@/lib/supabase";
import queryClient from "@/lib/tanstack-query/client";
import { useTeamStore } from "@/stores/team-store";
import type { Session } from "@supabase/supabase-js";
import { useEffect, useReducer } from "react";
import { AuthContext, type VerifyOtpParams } from "./auth.context";
import { analytics } from "@/lib/analytics";
import { useUser } from "@/lib/tanstack-query/users";
import { ClaimFinalizer } from "@/components/ClaimFinalizer";
import { toAuthContinue } from "@/lib/urls";

interface AuthState {
  userId: string | null;
  session: Session | null;
  isLoading: boolean;
}

const appBaseUrl = () =>
  __APP_ENV__ === "production" ? __APP_PROD_URL__ : __APP_URL__;

const absoluteAppUrl = (path: string) => `https://${appBaseUrl()}${path}`;

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { setCurrentTeam } = useTeamStore();
  const [{ userId, session, isLoading }, dispatch] = useReducer(
    (s: AuthState, a: Partial<AuthState>) => ({ ...s, ...a }),
    {
      userId: null,
      session: null,
      isLoading: true,
    },
  );
  const { data: user, isLoading: isLoadingUser } = useUser(userId ?? undefined);

  useEffect(() => {
    if (user?.id) {
      analytics.identify(user.id, user.interests);
    }
  }, [user?.id]);

  useEffect(() => {
    dispatch({ isLoading: true });
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        dispatch({
          session,
          userId: session?.user.id ?? null,
        });
      })
      .finally(() => dispatch({ isLoading: false }));

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      dispatch({ session, userId: session?.user.id ?? null });
    });
    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    dispatch({ isLoading: true });
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;

      dispatch({ session: data.session });
    } finally {
      dispatch({ isLoading: false });
    }
  };

  const signup = async (
    email: string,
    password: string,
    name: string,
    redirectTo?: string,
  ) => {
    dispatch({ isLoading: true });
    try {
      // No roles here: onboarding asks once, right after verification. The
      // account row is provisioned with an empty interest list.
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { name, interests: [] },
          emailRedirectTo: absoluteAppUrl(
            `/login${redirectTo ? `?redirect=${encodeURIComponent(redirectTo)}` : ""}`,
          ),
        },
      });
      if (error) throw error;
    } finally {
      dispatch({ isLoading: false });
    }
  };

  const resetPassword = async (email: string, redirectTo?: string) => {
    dispatch({ isLoading: true });
    try {
      // Land on SafeLinks-safe Continue (never verify on load / never /reset-password directly).
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: absoluteAppUrl(
          toAuthContinue({ type: "recovery", redirect: redirectTo, email }),
        ),
      });
      if (error) throw error;
    } finally {
      dispatch({ isLoading: false });
    }
  };

  const sendMagicLink = async (email: string, redirectTo?: string) => {
    dispatch({ isLoading: true });
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: absoluteAppUrl(
            toAuthContinue({ type: "magiclink", redirect: redirectTo, email }),
          ),
        },
      });
      if (error) throw error;
    } finally {
      dispatch({ isLoading: false });
    }
  };

  const verifyOtp = async ({
    type,
    email,
    token,
    tokenHash,
  }: VerifyOtpParams) => {
    dispatch({ isLoading: true });
    try {
      if (tokenHash) {
        const { data, error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type,
        });
        if (error) throw error;
        dispatch({
          session: data.session,
          userId: data.session?.user.id ?? null,
        });
        return;
      }

      if (!email || !token) {
        throw new Error("Email and code are required");
      }

      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type,
      });
      if (error) throw error;
      dispatch({
        session: data.session,
        userId: data.session?.user.id ?? null,
      });
    } finally {
      dispatch({ isLoading: false });
    }
  };

  const verifyResetPasswordToken = async (email: string, token: string) =>
    verifyOtp({ type: "recovery", email, token });

  const updatePassword = async (password: string) => {
    dispatch({ isLoading: true });
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
    } finally {
      dispatch({ isLoading: false });
    }
  };

  const logout = async () => {
    dispatch({ isLoading: true });
    try {
      console.log("Logging out user");

      await supabase.auth.signOut();
      dispatch({ session: null });
      analytics.reset();
      setCurrentTeam(null);
      queryClient.clear();

      console.log("Logout successful");
    } finally {
      dispatch({ isLoading: false });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        session,
        isLoading: isLoading || isLoadingUser,
        login,
        signup,
        logout,
        resetPassword,
        sendMagicLink,
        verifyOtp,
        verifyResetPasswordToken,
        updatePassword,
      }}
    >
      <ClaimFinalizer />
      {children}
    </AuthContext.Provider>
  );
};
